import { z } from 'zod';

export const RecommendedProductVariantDTOSchema = z
  .object({
    variant_id: z.array(z.string()).optional(),
    sku_code: z.array(z.string()).optional(),
    size: z.array(z.string()).optional(),
    standard_size: z.array(z.string()).optional(),
    mrp: z.array(z.number()).optional(),
    selling_price: z.array(z.number()).optional(),
    saleable: z.array(z.boolean()).optional(),
    in_stock: z.array(z.boolean()).optional(),
    in_stock_facilities: z.array(z.string()).optional(),
    jit_available: z.array(z.boolean()).optional(),
    jit_facilities: z.array(z.unknown()).optional(),
    mto_available: z.array(z.boolean()).optional(),
    mto_enabled: z.array(z.boolean()).optional(),
    mtc_available: z.array(z.boolean()).optional(),
    mtc_enabled: z.array(z.boolean()).optional(),
    enabled_for_meta: z.array(z.boolean()).optional(),
    enabled_for_online: z.array(z.boolean()).optional(),
    images: z.array(z.string()).optional(),
    occasions: z.array(z.string()).optional(),
    barcode: z.array(z.string()).optional().nullable(),
    in_stock_in_back_facilities: z.array(z.string()).optional().nullable(),
    in_stock_in_front_facilities: z.array(z.string()).optional().nullable(),
    in_stock_or_in_transit_to_facilities: z.array(z.string()).optional().nullable(),
    availability: z.array(z.string()).optional(),
    discount_filter_name: z.array(z.string()).optional(),
    facility_inventory: z
      .record(
        z.string(),
        z.object({
          availableInventory: z.number(),
          backInventory: z.number(),
          frontInventory: z.number(),
          incomingQuantity: z.number().optional(),
          outgoingQuantity: z.number().optional(),
        }),
      )
      .optional(),
    vendor_article_number: z.array(z.string()).optional(),
    vault_price: z.array(z.number()).nullable().optional(),
  })
  // Backend keeps adding fields in this object; keep parser forward-compatible.
  .passthrough();

export const RecommendedProductDTOSchema = z.object({
  brand: z.string().optional().default(''),
  category: z.string().optional().default(''),
  color_palette: z.union([z.string(), z.array(z.string()), z.null()]).optional(),
  color: z.string().optional().default(''),
  currency: z.string().optional(),
  customizable: z.string().optional().default(''),
  default_image: z.string(),
  description: z.string().optional().default(''),
  fabric: z.string().optional().default(''),
  garment_style: z.string().optional().default(''),
  handle: z.string(),
  id_brand_manager: z.coerce.number(),
  id: z.string(),
  images: z.array(z.string()).nullable().optional(),
  inventory: z.number().optional(),
  lk_style_number: z.string().optional().default(''),
  merch_type: z.string().optional().default(''),
  mrp: z.number().optional().default(0),
  occasions: z.array(z.string()).optional().default([]),
  price: z.number().optional().default(0),
  primary_color: z.string().optional().default(''),
  primary_occasion: z.string().optional().default(''),
  product_id: z.string(),
  product_link: z.string().optional().default(''),
  product_type: z.string().optional().default(''),
  season: z.string().optional().default(''),
  secondary_occasion: z.string().optional().default(''),
  shopify_status: z.string().optional(),
  size_depth_in_lk_or_jit: z.number().optional(),
  size_depth_in_lk: z.number().optional(),
  tags_claude: z.array(z.string()).optional(),
  tags_openai: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional().default([]),
  text_embedding: z.null().optional(),
  title: z.string().optional().default(''),
  variants: z.array(RecommendedProductVariantDTOSchema).optional().default([]),
  vendor_name: z.string().optional().default(''),
  vertex_image_embedding: z.null().optional(),
  product_metrics: z.object({
    offline_units_sold_last_30_days: z.number(),
  }),
});
// New style-search payloads include many dynamic ML-derived fields.

export type RecommendedProductDTOType = z.infer<typeof RecommendedProductDTOSchema>;

export const RecommendedProductsDTOSchema = z.array(RecommendedProductDTOSchema).nullable();

export type RecommendedProductsDTOType = z.infer<typeof RecommendedProductsDTOSchema>;

export const RecommendedFacetDTOSchema = z.object({
  field_name: z.string(),
  counts: z
    .array(
      z.object({
        value: z.union([z.string(), z.number(), z.boolean()]).transform(v => String(v)),
        count: z.number(),
      }),
    )
    .optional()
    .nullable(),
});

export type RecommendedFacetDTOType = z.infer<typeof RecommendedFacetDTOSchema>;

export const RecommendedFacetsDTOSchema = z.array(RecommendedFacetDTOSchema);

export type RecommendedFacetsDTOType = z.infer<typeof RecommendedFacetsDTOSchema>;
